import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/layout/Layout';
import { AccueilPage } from './pages/AccueilPage';
import { Dashboard } from './pages/Dashboard';
import { InvoicesPage } from './pages/InvoicesPage';
import { InvoiceValidationPage } from './pages/InvoiceValidationPage';
import { SuppliersPage } from './pages/SuppliersPage';
import { UtilisateursPage } from './pages/admin/UtilisateursPage';
import { ProfilsRolesPage } from './pages/admin/ProfilsRolesPage';
import { PaiementsParametresPage } from './pages/PaiementsParametresPage';
import { ExpertsAutoFacturationPage } from './pages/ExpertsAutoFacturationPage';
import { ExpertsHistoryPage } from './pages/ExpertsHistoryPage';
import { ComptaExportsPage } from './pages/ComptaExportsPage';
import { ComptaInitialisationPage } from './pages/ComptaInitialisationPage';
import { SentryTicketsPage } from './pages/SentryTicketsPage';
import { BeneficiairesPage } from './pages/BeneficiairesPage';
import { PaiementsAPayerPage } from './pages/PaiementsAPayerPage';
import { PaiementsValidationPage } from './pages/PaiementsValidationPage';
import { PaiementsRemboursementsPage } from './pages/PaiementsRemboursementsPage';
import { PaiementsExportPage } from './pages/PaiementsExportPage';
import { PaiementsEnCoursPage } from './pages/PaiementsEnCoursPage';
import { LoginPage } from './pages/LoginPage';
import { NdfSalariesPage } from './pages/ndf/NdfSalariesPage';
import { NdfParametragePage } from './pages/ndf/NdfParametragePage';
import { CongesParametragePage } from './pages/conges/CongesParametragePage';
import { MesCongesPage } from './pages/conges/MesCongesPage';
import { CongesReprisePage } from './pages/conges/CongesReprisePage';
import { CongesDemandePage } from './pages/conges/CongesDemandePage';
import { CongesDemandesPage } from './pages/conges/CongesDemandesPage';
import { CongesDemandeDetailPage } from './pages/conges/CongesDemandeDetailPage';
import { CongesCalendrierPage } from './pages/conges/CongesCalendrierPage';
import { CongesAValiderPage } from './pages/conges/CongesAValiderPage';
import { CongesCalendrierEquipePage } from './pages/conges/CongesCalendrierEquipePage';
import { CongesRegistrePage } from './pages/conges/CongesRegistrePage';
import { CongesTicketsPage } from './pages/conges/CongesTicketsPage';
import { CongesArretsPage } from './pages/conges/CongesArretsPage';
import { CongesCompteursPage } from './pages/conges/CongesCompteursPage';
import { CongesPaiePage } from './pages/conges/CongesPaiePage';
import { CongesJustificatifsPage } from './pages/conges/CongesJustificatifsPage';
import { NdfShell } from './components/ndf/NdfShell';
import { useNdfMoi } from './hooks/useNdfMoi';
import { NdfMesDepensesPage } from './pages/ndf/NdfMesDepensesPage';
import { NdfDepenseFormPage } from './pages/ndf/NdfDepenseFormPage';
import { NdfComparerPage } from './pages/ndf/NdfComparerPage';
import { NdfTrajetFormPage } from './pages/ndf/NdfTrajetFormPage';
import { NdfNoteDetailPage } from './pages/ndf/NdfNoteDetailPage';
import { NdfAValiderPage } from './pages/ndf/NdfAValiderPage';
import { NdfPilotagePage } from './pages/ndf/NdfPilotagePage';
import { NdfTableauBordPage } from './pages/ndf/NdfTableauBordPage';
import { NdfDepenseDetailPage } from './pages/ndf/NdfDepenseDetailPage';
import { NdfMesNotesPage } from './pages/ndf/NdfMesNotesPage';
import { NdfMoiPage } from './pages/ndf/NdfMoiPage';
import { ToastProvider } from './context/ToastContext';
import { UserProvider, useUser } from './context/UserContext';
import { RequireAuth } from './components/auth/RequireAuth';
import { RequireAdmin } from './components/auth/RequireAdmin';
import { RequireAdminOrValidator } from './components/auth/RequireAdminOrValidator';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Où atterrit-on en arrivant sur Gertrude ?
 *
 * Sur l'ACCUEIL, désormais, et non plus dans un module choisi par une cascade
 * de rôles. Cette cascade envoyait tout administrateur sur les factures
 * fournisseurs — y compris un manager qui ne vient que pour approuver des notes
 * de frais. Avec plusieurs modules et bientôt les congés, personne ne porte
 * plus une seule casquette : le choix revient à la personne.
 *
 * Seul cas conservé : sans aucun accès, retour à la connexion.
 */
function RootRedirect() {
  const { user } = useUser();
  const ndf = useNdfMoi();
  if (user === undefined || ndf.chargement) return null;
  if (user?.role === 'admin' || user?.is_payment_validator || ndf.aAcces) {
    return <Navigate to="/accueil" replace />;
  }
  return <Navigate to="/login" replace />;
}

/** Redirige une ancienne URL de détail facture en conservant l'id + la query. */
function RedirectInvoiceDetail() {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/factures/liste/${id}${search}`} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <UserProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route
                element={
                  <RequireAuth>
                    <Layout />
                  </RequireAuth>
                }
              >
                <Route index element={<RootRedirect />} />
                <Route path="accueil" element={<AccueilPage />} />

                {/* ── Factures fournisseurs (admin) ── */}
                <Route path="factures">
                  <Route index element={<Navigate to="/factures/dashboard" replace />} />
                  <Route path="dashboard" element={<RequireAdmin><Dashboard /></RequireAdmin>} />
                  <Route path="liste" element={<RequireAdmin><InvoicesPage /></RequireAdmin>} />
                  <Route path="liste/:id" element={<RequireAdmin><InvoiceValidationPage /></RequireAdmin>} />
                  <Route path="fournisseurs" element={<RequireAdmin><SuppliersPage /></RequireAdmin>} />
                </Route>

                {/* ── Auto-facturation (admin) ── */}
                <Route path="auto-facturation">
                  <Route index element={<Navigate to="/auto-facturation/nouvelle" replace />} />
                  <Route path="nouvelle" element={<RequireAdmin><ExpertsAutoFacturationPage /></RequireAdmin>} />
                  <Route path="historique" element={<RequireAdmin><ExpertsHistoryPage /></RequireAdmin>} />
                </Route>

                {/* ── Exports comptables KPMG (admin) ── */}
                <Route path="exports-comptables">
                  <Route index element={<RequireAdmin><ComptaExportsPage /></RequireAdmin>} />
                  <Route path="initialisation" element={<RequireAdmin><ComptaInitialisationPage /></RequireAdmin>} />
                </Route>

                {/* ── Paiements ── */}
                <Route path="paiements">
                  <Route index element={<Navigate to="/paiements/a-payer" replace />} />
                  <Route path="beneficiaires" element={<RequireAdmin><BeneficiairesPage /></RequireAdmin>} />
                  <Route path="a-payer" element={<RequireAdmin><PaiementsAPayerPage /></RequireAdmin>} />
                  <Route path="a-valider" element={<RequireAdminOrValidator><PaiementsValidationPage /></RequireAdminOrValidator>} />
                  <Route path="remboursements" element={<RequireAdmin><PaiementsRemboursementsPage /></RequireAdmin>} />
                  <Route path="exporter" element={<RequireAdmin><PaiementsExportPage /></RequireAdmin>} />
                  {/* ADMIN, comme le menu et l’API : la laisser ouverte aux valideurs
                      menait sur un écran qui se remplit d’un refus (audit du 14/10/2026). */}
                  <Route path="en-cours" element={<RequireAdmin><PaiementsEnCoursPage /></RequireAdmin>} />
                  <Route path="parametres" element={<RequireAdmin><PaiementsParametresPage /></RequireAdmin>} />
                </Route>

                {/* ── Espace collaborateur : le shell s'ajoute au Layout ── */}
                <Route element={<NdfShell />}>
                  <Route path="/notes-de-frais/mes-depenses" element={<NdfMesDepensesPage />} />
                  <Route path="/notes-de-frais/depense/nouvelle" element={<NdfDepenseFormPage />} />
                  <Route path="/notes-de-frais/trajet/nouveau" element={<NdfTrajetFormPage />} />
                  <Route path="/notes-de-frais/depense/:id" element={<NdfDepenseDetailPage />} />
                  <Route
                    path="/notes-de-frais/comparer/:id/:autreId"
                    element={<NdfComparerPage />}
                  />
                  <Route path="/notes-de-frais/mes-notes" element={<NdfMesNotesPage />} />
                  <Route path="/notes-de-frais/note/:id" element={<NdfNoteDetailPage />} />
                  <Route path="/notes-de-frais/a-valider" element={<NdfAValiderPage />} />
                  <Route path="/notes-de-frais/pilotage" element={<NdfPilotagePage />} />
                  <Route path="/notes-de-frais/tableau-de-bord" element={<NdfTableauBordPage />} />
                  <Route path="/notes-de-frais/moi" element={<NdfMoiPage />} />
                </Route>

                {/* ── Notes de frais (Lot 1 : socle RH et paramétrage) ── */}
                {/* Les gardes fines (profils, périmètre de visibilité) sont posées
                    côté serveur : l'écran ne décide jamais de ce qui est autorisé. */}
                <Route path="notes-de-frais">
                  <Route index element={<Navigate to="/notes-de-frais/mes-depenses" replace />} />
                  {/* L'onglet Salariés a rejoint « Ressources humaines » (décision
                      N1). L'ancienne adresse REDIRIGE au lieu de disparaître :
                      elle est dans les favoris de Valérie et dans des messages
                      déjà envoyés. Un lien mort n'apprend rien à qui le suit. */}
                  <Route path="salaries" element={<Navigate to="/ressources-humaines/salaries" replace />} />
                  <Route path="parametrage" element={<NdfParametragePage />} />
                </Route>

                {/* ── Ressources humaines : le référentiel partagé ── */}
                <Route path="ressources-humaines">
                  <Route index element={<Navigate to="/ressources-humaines/salaries" replace />} />
                  <Route path="salaries" element={<NdfSalariesPage />} />
                </Route>

                {/* ── Congés (Lots 2-3 : soldes, reprise, demandes et calendrier) ── */}
                <Route path="conges">
                  <Route index element={<Navigate to="/conges/mes-conges" replace />} />
                  <Route path="mes-conges" element={<MesCongesPage />} />
                  <Route path="calendrier" element={<CongesCalendrierPage />} />
                  <Route path="poser" element={<CongesDemandePage />} />
                  <Route path="demandes" element={<CongesDemandesPage />} />
                  <Route path="a-valider" element={<CongesAValiderPage />} />
                  <Route path="calendrier-equipe" element={<CongesCalendrierEquipePage />} />
                  <Route path="registre" element={<CongesRegistrePage />} />
                  <Route path="compteurs" element={<CongesCompteursPage />} />
                  <Route path="arrets" element={<CongesArretsPage />} />
                  <Route path="tickets" element={<CongesTicketsPage />} />
                  <Route path="justificatifs" element={<CongesJustificatifsPage />} />
                  <Route path="paie" element={<CongesPaiePage />} />
                  <Route path="demandes/:id" element={<CongesDemandeDetailPage />} />
                  <Route path="reprise" element={<CongesReprisePage />} />
                  <Route path="parametrage" element={<CongesParametragePage />} />
                </Route>

                {/* ── Support (admin) ── */}
                <Route path="support">
                  <Route index element={<Navigate to="/support/tickets-dev" replace />} />
                  <Route path="tickets-dev" element={<RequireAdmin><SentryTicketsPage /></RequireAdmin>} />
                </Route>

                {/* ── Admin ── */}
                <Route path="admin">
                  <Route index element={<Navigate to="/admin/utilisateurs" replace />} />
                  <Route path="utilisateurs" element={<RequireAdmin><UtilisateursPage /></RequireAdmin>} />
                  <Route path="profils" element={<RequireAdmin><ProfilsRolesPage /></RequireAdmin>} />
                  {/* Ces réglages ont rejoint le module Paiements : un ancien lien y mène. */}
                  <Route path="parametres" element={<Navigate to="/paiements/parametres" replace />} />
                </Route>

                {/* ── Redirections anciennes routes → nouvelles ── */}
                <Route path="invoices" element={<Navigate to="/factures/liste" replace />} />
                <Route path="invoices/:id" element={<RedirectInvoiceDetail />} />
                <Route path="suppliers" element={<Navigate to="/factures/fournisseurs" replace />} />
                <Route path="experts/auto-facturation" element={<Navigate to="/auto-facturation/nouvelle" replace />} />
                <Route path="experts/historique" element={<Navigate to="/auto-facturation/historique" replace />} />
                <Route path="beneficiaires" element={<Navigate to="/paiements/beneficiaires" replace />} />
                <Route path="paiements/validation" element={<Navigate to="/paiements/a-valider" replace />} />
                <Route path="paiements/export" element={<Navigate to="/paiements/exporter" replace />} />
                <Route path="tickets-dev" element={<Navigate to="/support/tickets-dev" replace />} />
                <Route path="users" element={<Navigate to="/admin/utilisateurs" replace />} />

                <Route path="*" element={<p className="text-slate-500">Page non trouvée.</p>} />
              </Route>
            </Routes>
          </BrowserRouter>
        </UserProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
